import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators, type ValidatorFn } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { marked } from 'marked';
import {
  customPropertyReferenceEntityTypeValues,
  type CustomPropertyDefinitionResponse,
  type CustomPropertyReferenceEntityType,
  type CustomPropertyType,
} from '@zag/core/models';
import { forkJoin, of, type Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AccordionItemComponent } from '../accordion/index';
import { FormField } from '../form-field/form-field';
import { Input as ZagInput } from '../input/input';
import { NumberInput } from '../number-input/index';
import { Checkbox } from '../checkbox/checkbox';
import { DatePicker } from '../date-picker/index';
import { Dropdown, type DropdownOption } from '../dropdown/dropdown';
import { MultiSelect } from '../multi-select/multi-select';
import { TagsInput } from '../tags-input/tags-input';
import { ErrorStateComponent } from '../error-state/error-state.component';
import { ZagIcon } from '../icon/icon';
import { ToastService } from '../toast/toast.service';
import { TriStateFieldComponent, type TriStateMode } from '../bulk-edit/tri-state-field.component';
import { getFieldError } from '../utils/form-error.utils';
import { extractHttpErrorMessage } from '../utils/http-error.utils';
import { CustomPropertyDefinitionsService } from '../services/custom-property-definitions.service';
import { LanguageService } from '../services/language.service';

interface ReferenceOption extends DropdownOption<string> {
  description?: string | null;
  color?: string;
  imageUrl?: string | null;
  data?: Record<string, unknown> | null;
}

interface ReferenceOptionResponse {
  value: string;
  label: string;
  icon: string;
  description: string | null;
  color: string | null;
  imageUrl?: string | null;
  data?: Record<string, unknown> | null;
}

export interface CustomPropertyOperation {
  op: 'set' | 'clear';
  value?: unknown;
}

function getReferenceEntityType(def: CustomPropertyDefinitionResponse): CustomPropertyReferenceEntityType | null {
  const value = (def.config as Record<string, unknown> | null)?.['referenceEntityType'];
  return value === 'location' || value === 'skill' ? value : null;
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

const nonEmptyArrayValidator: ValidatorFn = control => {
  const value = control.value;
  if (Array.isArray(value) && value.length === 0) {
    return { required: true };
  }
  return null;
};

/**
 * Normalises a reference custom-property value into a list of trimmed,
 * non-empty ids. Accepts a single string, an array of strings, or null —
 * unknown shapes return an empty list.
 */
function collectReferenceIds(value: unknown): string[] {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? [trimmed] : [];
  }
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0).map(v => v.trim());
  }
  return [];
}

@Component({
  selector: 'zag-custom-properties-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents makes this element transparent to the parent accordion's flex layout,
  // so each zag-accordion-item rendered here participates in the parent accordion's gap.
  host: { style: 'display: contents' },
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    AccordionItemComponent,
    FormField,
    ZagInput,
    NumberInput,
    Checkbox,
    DatePicker,
    Dropdown,
    MultiSelect,
    TagsInput,
    ErrorStateComponent,
    ZagIcon,
    TriStateFieldComponent,
  ],
  styles: [
    `
      .zag-custom-properties-form__reference-display {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        min-height: 2.5rem;
        padding: var(--spacing-sm) var(--spacing-md);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        background: var(--surface-color-default);
      }

      .zag-custom-properties-form__reference-avatar {
        width: 2rem;
        height: 2rem;
        border-radius: 999px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--background-color-secondary);
        color: var(--text-color-secondary);
        flex-shrink: 0;
        overflow: hidden;
      }

      .zag-custom-properties-form__reference-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .zag-custom-properties-form__reference-avatar--colored {
        color: var(--color-white);
      }

      .zag-custom-properties-form__reference-content {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .zag-custom-properties-form__reference-label {
        font-size: 0.875rem;
        color: var(--text-color-primary);
      }

      .zag-custom-properties-form__reference-description {
        font-size: 0.75rem;
        color: var(--text-color-secondary);
      }

      .zag-custom-properties-form__reference-empty {
        color: var(--text-color-tertiary);
        font-size: 0.875rem;
      }

      .zag-custom-properties-form__reference-type-badge {
        width: fit-content;
        padding: 1px var(--spacing-xs);
        border-radius: var(--border-radius-sm);
        background: var(--background-color-secondary);
        color: var(--text-color-secondary);
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }

      .zag-custom-properties-form__reference-map {
        width: 100%;
        border-radius: var(--border-radius-md);
        border: 1px solid var(--border-color-default);
        background: var(--background-color-secondary);
      }

      .zag-custom-properties-form__reference-info {
        padding: var(--spacing-sm) var(--spacing-md);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        background: var(--surface-color-default);
        color: var(--text-color-primary);
        font-size: var(--font-size-sm);

        :where(h1, h2, h3, h4, h5, h6) {
          margin: 0 0 var(--spacing-xs);
          font-size: var(--font-size-body);
          font-weight: 600;
        }

        :where(p, ul, ol) {
          margin: 0 0 var(--spacing-xs);
        }

        :where(p:last-child, ul:last-child, ol:last-child) {
          margin-bottom: 0;
        }
      }

      .zag-custom-properties-form__switch {
        position: relative;
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        cursor: pointer;

        input[type='checkbox'] {
          position: absolute;
          top: 0;
          left: 0;
          opacity: 0;
          pointer-events: none;
          width: 100%;
          height: 100%;
          margin: 0;
        }

        &:has(input:disabled) {
          cursor: not-allowed;
          opacity: var(--opacity-disabled, 0.6);
        }
      }

      .zag-custom-properties-form__switch-track {
        position: relative;
        display: inline-block;
        width: 36px;
        height: 20px;
        border-radius: var(--border-radius-full);
        background: var(--color-neutral-2);
        transition: background-color var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-custom-properties-form__switch-thumb {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: var(--color-neutral-0);
        box-shadow: var(--shadow-sm);
        transition: transform var(--transition-duration-fast) var(--transition-timing);
      }

      .zag-custom-properties-form__switch input:checked + .zag-custom-properties-form__switch-track {
        background: var(--color-primary);

        .zag-custom-properties-form__switch-thumb {
          transform: translateX(16px);
        }
      }

      .zag-custom-properties-form__switch input:focus-visible + .zag-custom-properties-form__switch-track {
        box-shadow: var(--button-primary-focus-shadow);
      }
    `,
  ],
  template: `
    @if (loadErrorMessage(); as errorMessage) {
      <zag-error-state [message]="errorMessage" />
    } @else {
      @for (group of definitionGroups(); track group.sectionKey) {
        <zag-accordion-item
          [title]="group.title"
          [icon]="group.icon"
          [expanded]="isSectionExpanded(group.sectionKey)"
          (expandedChange)="onSectionExpandedChange(group.sectionKey, $event)"
        >
          <label slot="actions" class="zag-custom-properties-form__switch">
            <input
              type="checkbox"
              role="switch"
              [checked]="isSectionEnabled(group.sectionKey)"
              [disabled]="disabled()"
              [attr.aria-label]="group.title"
              (change)="onSectionToggle(group.sectionKey, $any($event.target).checked)"
            />
            <span class="zag-custom-properties-form__switch-track" aria-hidden="true">
              <span class="zag-custom-properties-form__switch-thumb"></span>
            </span>
          </label>
          @for (def of group.definitions; track def.id) {
            <zag-tri-state-field [showMode]="bulkEdit()" [mode]="getMode(def.key)" (modeChange)="setMode(def.key, $event)">
              @switch (def.propertyType) {
                @case ('text') {
                  <zag-form-field
                    [label]="def.label"
                    [error]="getError(def.key)"
                    [required]="isEffectivelyRequired(def)"
                    [forId]="'cp-' + def.key"
                  >
                    <zag-input
                      [id]="'cp-' + def.key"
                      [formControl]="getControl(def.key)"
                      [disabled]="disabled()"
                      [placeholder]="getConfigValue(def, 'placeholder') ?? ''"
                      [multiline]="getConfigBoolean(def, 'multiline')"
                    />
                  </zag-form-field>
                }
                @case ('number') {
                  <zag-form-field
                    [label]="def.label"
                    [error]="getError(def.key)"
                    [required]="isEffectivelyRequired(def)"
                    [forId]="'cp-' + def.key"
                  >
                    <zag-number-input
                      [id]="'cp-' + def.key"
                      [formControl]="getControl(def.key)"
                      [disabled]="disabled()"
                      [min]="getConfigNumber(def, 'min')"
                      [max]="getConfigNumber(def, 'max')"
                      [step]="getConfigNumber(def, 'step') ?? 1"
                      [suffix]="getConfigValue(def, 'suffix') ?? ''"
                    />
                  </zag-form-field>
                }
                @case ('boolean') {
                  <zag-form-field [label]="def.label" [error]="getError(def.key)" [forId]="'cp-' + def.key">
                    <zag-checkbox [id]="'cp-' + def.key" [formControl]="getControl(def.key)" [disabled]="disabled()" />
                  </zag-form-field>
                }
                @case ('date') {
                  <zag-form-field
                    [label]="def.label"
                    [error]="getError(def.key)"
                    [required]="isEffectivelyRequired(def)"
                    [forId]="'cp-' + def.key"
                  >
                    <zag-date-picker [id]="'cp-' + def.key" [formControl]="getControl(def.key)" [disabled]="disabled()" />
                  </zag-form-field>
                }
                @case ('select') {
                  <zag-form-field
                    [label]="def.label"
                    [error]="getError(def.key)"
                    [required]="isEffectivelyRequired(def)"
                    [forId]="'cp-' + def.key"
                  >
                    <zag-dropdown
                      [id]="'cp-' + def.key"
                      [formControl]="getControl(def.key)"
                      [options]="getSelectOptions(def)"
                      [disabled]="disabled()"
                      [clearable]="!isEffectivelyRequired(def)"
                    />
                  </zag-form-field>
                }
                @case ('multi-select') {
                  <zag-form-field
                    [label]="def.label"
                    [error]="getError(def.key)"
                    [required]="isEffectivelyRequired(def)"
                    [forId]="'cp-' + def.key"
                  >
                    <zag-multi-select
                      [id]="'cp-' + def.key"
                      [formControl]="getControl(def.key)"
                      [options]="getSelectOptions(def)"
                      [disabled]="disabled()"
                      [searchable]="true"
                      [placeholder]="'common.select' | translate"
                    />
                  </zag-form-field>
                }
                @case ('tags') {
                  <zag-form-field [label]="def.label" [error]="getError(def.key)" [forId]="'cp-' + def.key">
                    <zag-tags-input [id]="'cp-' + def.key" [formControl]="getControl(def.key)" [disabled]="disabled()" />
                  </zag-form-field>
                }
                @case ('reference') {
                  <zag-form-field
                    [label]="def.label"
                    [error]="getError(def.key)"
                    [required]="isEffectivelyRequired(def)"
                    [forId]="'cp-' + def.key"
                  >
                    @if (disabled()) {
                      @let selected = getSelectedReferenceOptions(def);
                      @if (selected.length === 0) {
                        <div class="zag-custom-properties-form__reference-display">
                          <span class="zag-custom-properties-form__reference-empty">{{ 'common.empty' | translate }}</span>
                        </div>
                      } @else {
                        @for (option of selected; track option.value) {
                          <div class="zag-custom-properties-form__reference-display">
                            <div
                              class="zag-custom-properties-form__reference-avatar"
                              [class.zag-custom-properties-form__reference-avatar--colored]="!!option.color"
                              [style.background-color]="option.color || null"
                            >
                              @if (option.imageUrl) {
                                <img class="zag-custom-properties-form__reference-image" [src]="option.imageUrl" alt="" />
                              } @else {
                                <span [zagIcon]="option.icon || 'link'"></span>
                              }
                            </div>
                            <div class="zag-custom-properties-form__reference-content">
                              <span class="zag-custom-properties-form__reference-label">{{ option.label }}</span>
                              @if (option.description) {
                                <span class="zag-custom-properties-form__reference-description">{{ option.description }}</span>
                              }
                              @if (getReferenceOptionCustomProperty(option, 'type'); as refType) {
                                <span class="zag-custom-properties-form__reference-type-badge">{{ refType }}</span>
                              }
                            </div>
                          </div>
                          @if (getReferenceOptionCustomProperty(option, 'map'); as mapUrl) {
                            <img class="zag-custom-properties-form__reference-map" [src]="mapUrl" alt="" />
                          }
                          @if (renderMarkdown(getReferenceOptionCustomProperty(option, 'destinationInfo')); as infoHtml) {
                            <div class="zag-custom-properties-form__reference-info" [innerHTML]="infoHtml"></div>
                          }
                        }
                      }
                    } @else if (isMultiReference(def)) {
                      <zag-multi-select
                        [id]="'cp-' + def.key"
                        [formControl]="getControl(def.key)"
                        [options]="getReferenceOptions(def)"
                        [disabled]="disabled()"
                        [searchable]="true"
                        [placeholder]="'common.select' | translate"
                      />
                    } @else {
                      <zag-dropdown
                        [id]="'cp-' + def.key"
                        [formControl]="getControl(def.key)"
                        [options]="getReferenceOptions(def)"
                        [disabled]="disabled()"
                        [clearable]="!isEffectivelyRequired(def)"
                      />
                    }
                  </zag-form-field>
                }
              }
            </zag-tri-state-field>
          }
        </zag-accordion-item>
      }
    }
  `,
})
export class CustomPropertiesFormComponent {
  private readonly definitionsApi = inject(CustomPropertyDefinitionsService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });
  private readonly toast = inject(ToastService);
  private readonly languageService = inject(LanguageService);

  /** Entity type to load definitions for (e.g. 'calendar', 'person') */
  readonly entityType = input.required<string>();

  /** Initial custom property values from the entity's data.customProperties */
  readonly values = input<Record<string, unknown>>({});

  /** Whether the form should be disabled (read-only mode) */
  readonly disabled = input(false);

  /** Render each property as an explicit unchanged/set/clear operation. */
  readonly bulkEdit = input(false);

  /**
   * Active booking type — when set, custom properties with matching
   * `config.requiredForBookingTypes` are treated as required even
   * if `def.required` is false.
   */
  readonly bookingType = input<string | null>(null);

  /** Emitted whenever the form values change */
  readonly valuesChange = output<Record<string, unknown>>();

  /** Emitted whenever a bulk property operation changes. */
  readonly operationsChange = output<Record<string, CustomPropertyOperation>>();

  private readonly definitionsResource = rxResource({
    params: () => ({ entityType: this.entityType(), lang: this.languageService.currentLanguage() }),
    stream: ({ params }) => this.definitionsApi.list(params.entityType),
  });

  private readonly referenceOptionsResource = rxResource({
    params: () => this.referenceEntityTypes().join('|'),
    stream: ({ params }) => {
      if (!params) {
        return of({} as Record<string, ReferenceOption[]>);
      }

      return this.loadReferenceOptions(params.split('|') as CustomPropertyReferenceEntityType[]);
    },
  });

  /** Loaded definitions for this entity type */
  readonly definitions = computed(() => {
    // Guard against ResourceValueError thrown by value() when resource is in error state
    if (this.definitionsResource.error()) return [];
    return this.definitionsResource.value() ?? [];
  });

  /** Whether the initial definition request has finished (success or error) */
  readonly isReady = computed(() => !this.definitionsResource.isLoading());

  /** Inline load error shown when definitions cannot be retrieved */
  readonly loadErrorMessage = computed<string | null>(() => {
    this.currentLang();
    const error = this.definitionsResource.error();
    if (!error) return null;
    return extractHttpErrorMessage(error, this.translate.instant('common.error'));
  });

  readonly hasLoadError = computed(() => this.loadErrorMessage() !== null);

  readonly referenceEntityTypes = computed(() => {
    const entityTypes = new Set<CustomPropertyReferenceEntityType>();

    for (const definition of this.definitions()) {
      const referenceEntityType = getReferenceEntityType(definition);
      if (referenceEntityType) {
        entityTypes.add(referenceEntityType);
      }
    }

    return Array.from(entityTypes).sort((a, b) => a.localeCompare(b));
  });

  private controlsMap = new Map<string, FormControl>();
  private readonly _controlsValid = signal(true);
  private readonly _sectionEnabledMap = signal<Record<string, boolean>>({});
  private readonly _sectionExpandedMap = signal<Record<string, boolean>>({});
  private readonly modes = signal<Record<string, TriStateMode>>({});
  private readonly operations = signal<Record<string, CustomPropertyOperation>>({});

  isSectionEnabled(sectionKey: string): boolean {
    return this._sectionEnabledMap()[sectionKey] ?? false;
  }

  isSectionExpanded(sectionKey: string): boolean {
    return this._sectionExpandedMap()[sectionKey] ?? false;
  }

  onSectionToggle(sectionKey: string, enabled: boolean): void {
    this._sectionEnabledMap.update(m => ({ ...m, [sectionKey]: enabled }));
    if (enabled) {
      this._sectionExpandedMap.update(m => ({ ...m, [sectionKey]: true }));
    } else {
      this._sectionExpandedMap.update(m => ({ ...m, [sectionKey]: false }));
      this.resetSectionControls(sectionKey);
    }
    this.updateValidators(this.definitions());
    this.valuesChange.emit(this.getValues());
  }

  onSectionExpandedChange(sectionKey: string, expanded: boolean): void {
    this._sectionExpandedMap.update(m => ({ ...m, [sectionKey]: expanded }));
    if (expanded && !this.isSectionEnabled(sectionKey)) {
      this._sectionEnabledMap.update(m => ({ ...m, [sectionKey]: true }));
      this.updateValidators(this.definitions());
      this.valuesChange.emit(this.getValues());
    }
  }

  private resetSectionControls(sectionKey: string): void {
    const group = this.definitionGroups().find(g => g.sectionKey === sectionKey);
    if (!group) return;
    for (const def of group.definitions) {
      const control = this.controlsMap.get(def.key);
      if (control) {
        control.setValue(this.getDefaultValue(def), { emitEvent: false });
        control.markAsPristine();
        control.markAsUntouched();
      }
    }
  }

  /** Whether the form has unsaved changes */
  readonly isDirty = signal(false);

  /** Whether the form is valid */
  readonly isValid = computed(() => {
    if (this.definitionsResource.error()) return false;
    for (const group of this.definitionGroups()) {
      if (!this.isSectionEnabled(group.sectionKey)) continue;
      for (const def of group.definitions) {
        const control = this.controlsMap.get(def.key);
        if (control && !control.valid) return false;
      }
    }
    return true;
  });

  /**
   * Definitions grouped by sectionTitle so each group becomes one accordion item.
   * Definitions without a sectionTitle share a default "Custom Properties" group.
   * Order within each group follows the definition's priority.
   */
  readonly definitionGroups = computed(() => {
    this.currentLang();
    const defs = this.definitions();
    const defaultTitle = this.translate.instant('common.customProperties.sectionTitle');
    const groupMap = new Map<string, { title: string; icon: string; definitions: CustomPropertyDefinitionResponse[] }>();

    for (const def of defs) {
      const key = def.sectionTitle ?? '';
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          title: def.sectionTitle ?? defaultTitle,
          icon: def.sectionIcon ?? 'tune',
          definitions: [],
        });
      }
      const group = groupMap.get(key);
      if (group) {
        group.definitions.push(def);
      }
    }

    return Array.from(groupMap.entries()).map(([sectionKey, group]) => ({ sectionKey, ...group }));
  });

  constructor() {
    // Build form reactively when definitions load (guard against error state).
    // Why: NOT tracking `this.values()` here — buildForm emits `valuesChange`
    // which the parent typically feeds back into its FormControl; tracking the
    // input would then re-run this effect, re-emit, and loop (NG0103). buildForm
    // already reads the current values via `this.values()` inside `untracked`.
    effect(() => {
      if (this.definitionsResource.error()) return;
      const defs = this.definitionsResource.value();
      if (defs) {
        untracked(() => this.buildForm(defs));
      }
    });

    // Re-evaluate validators when bookingType changes
    effect(() => {
      this.bookingType(); // track
      const defs = this.definitions();
      if (defs.length > 0) {
        untracked(() => this.updateValidators(defs));
      }
    });

    effect(() => {
      const message = this.loadErrorMessage();
      if (message) {
        untracked(() => this.toast.error(message));
      }
    });
  }

  markAllAsTouched(): void {
    for (const group of this.definitionGroups()) {
      if (this.isSectionEnabled(group.sectionKey)) {
        for (const def of group.definitions) {
          const control = this.controlsMap.get(def.key);
          control?.markAsTouched();
        }
      }
    }

    this.updateFormState();
  }

  /** Returns current form values as a key-value map */
  getValues(): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const group of this.definitionGroups()) {
      if (!this.isSectionEnabled(group.sectionKey)) {
        continue;
      }
      for (const def of group.definitions) {
        const control = this.controlsMap.get(def.key);
        if (control) {
          result[def.key] = control.value;
        }
      }
    }
    return result;
  }

  /**
   * Returns a map of property key → resolved display label for each custom
   * property that has a non-trivial label (reference options, select options,
   * or the definition label itself). Used to persist human-readable labels
   * alongside raw values so tooltips and summaries can show names instead of UUIDs.
   */
  getResolvedLabels(): Record<string, { label: string; value: string }> {
    const result: Record<string, { label: string; value: string }> = {};
    for (const group of this.definitionGroups()) {
      if (!this.isSectionEnabled(group.sectionKey)) {
        continue;
      }
      for (const def of group.definitions) {
        const control = this.controlsMap.get(def.key);
        const rawValue = control?.value;
        if (isEmptyValue(rawValue)) continue;

        const resolved = this.resolveLabelForDefinition(def, rawValue);
        if (resolved !== null) {
          result[def.key] = { label: def.label, value: resolved };
        }
      }
    }
    return result;
  }

  private resolveLabelForDefinition(def: CustomPropertyDefinitionResponse, rawValue: unknown): string | null {
    if (getReferenceEntityType(def)) {
      const refOptions = this.getSelectedReferenceOptions(def);
      if (refOptions.length > 0) return refOptions.map(o => o.label).join(', ');
    }

    if (def.propertyType === 'multi-select' && Array.isArray(rawValue)) {
      const byValue = new Map(this.getSelectOptions(def).map(o => [o.value, o.label]));
      return rawValue.map(v => byValue.get(String(v)) ?? String(v)).join(', ');
    }

    if (def.propertyType === 'select') {
      const selected = this.getSelectOptions(def).find(o => o.value === rawValue);
      if (selected) return selected.label;
    }

    return Array.isArray(rawValue) ? rawValue.join(', ') : String(rawValue);
  }

  getControl(key: string): FormControl {
    return this.controlsMap.get(key) ?? new FormControl(null);
  }

  getMode(key: string): TriStateMode {
    return this.modes()[key] ?? 'unchanged';
  }

  setMode(key: string, mode: TriStateMode): void {
    this.modes.update(current => ({ ...current, [key]: mode }));
    this.updateOperation(key);
    this.updateValidators(this.definitions());
  }

  getError(key: string): string {
    const control = this.controlsMap.get(key);
    return getFieldError(control, this.translate) ?? '';
  }

  getSelectOptions(def: CustomPropertyDefinitionResponse): DropdownOption<string>[] {
    const config = def.config as Record<string, unknown> | null;
    const options = (config?.['options'] as { value: string; label: string }[] | undefined) ?? [];
    return options.map(o => ({ label: o.label, value: o.value }));
  }

  getReferenceOptions(def: CustomPropertyDefinitionResponse): DropdownOption<string>[] {
    const referenceEntityType = getReferenceEntityType(def);
    if (!referenceEntityType) {
      return [];
    }

    return this.referenceOptionsResource.value()?.[referenceEntityType] ?? [];
  }

  getSelectedReferenceOption(def: CustomPropertyDefinitionResponse): ReferenceOption | null {
    const controlValue = this.getControl(def.key).value;
    if (typeof controlValue !== 'string' || controlValue.trim().length === 0) {
      return null;
    }

    return (
      (this.getReferenceOptions(def).find(option => option.value === controlValue) as ReferenceOption | undefined) ?? {
        value: controlValue,
        label: controlValue,
        icon: 'link',
      }
    );
  }

  /**
   * Returns the currently selected reference options as a flat list. Single
   * references resolve to a one-element array; multi-references resolve to one
   * option per stored id (with a synthetic fallback for unknown ids).
   */
  getSelectedReferenceOptions(def: CustomPropertyDefinitionResponse): ReferenceOption[] {
    const controlValue = this.getControl(def.key).value;
    const ids = collectReferenceIds(controlValue);
    if (ids.length === 0) return [];
    const options = this.getReferenceOptions(def) as ReferenceOption[];
    const byId = new Map(options.map(opt => [opt.value, opt]));
    return ids.map(id => byId.get(id) ?? { value: id, label: id, icon: 'link' });
  }

  isMultiReference(def: CustomPropertyDefinitionResponse): boolean {
    const config = def.config as Record<string, unknown> | null;
    return def.propertyType === 'reference' && config?.['multiple'] === true;
  }

  /**
   * Returns a named string custom property from the option's data.customProperties,
   * or null when absent. Used to render enriched reference option content.
   */
  getReferenceOptionCustomProperty(option: ReferenceOption, key: string): string | null {
    const customProperties = option.data?.['customProperties'];
    if (!customProperties || typeof customProperties !== 'object' || Array.isArray(customProperties)) {
      return null;
    }

    const value = (customProperties as Record<string, unknown>)[key];
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
  }

  /** Renders a markdown string to sanitized SafeHtml. Returns null for empty input. */
  renderMarkdown(text: string | null): SafeHtml | null {
    if (!text) {
      return null;
    }

    try {
      const html = marked.parse(text) as string;
      const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
      return this.sanitizer.bypassSecurityTrustHtml(sanitized);
    } catch {
      const escaped = text.replaceAll('<', '&lt;').replaceAll('>', '&gt;');
      const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, escaped) ?? '';
      return this.sanitizer.bypassSecurityTrustHtml(sanitized);
    }
  }

  getConfigValue(def: CustomPropertyDefinitionResponse, key: string): string | null {
    const config = def.config as Record<string, unknown> | null;
    const value = config?.[key];
    return typeof value === 'string' ? value : null;
  }

  getConfigBoolean(def: CustomPropertyDefinitionResponse, key: string): boolean {
    const config = def.config as Record<string, unknown> | null;
    return config?.[key] === true;
  }

  getConfigNumber(def: CustomPropertyDefinitionResponse, key: string): number | undefined {
    const config = def.config as Record<string, unknown> | null;
    const value = config?.[key];
    return typeof value === 'number' ? value : undefined;
  }

  private computeSectionMaps(currentValues: Record<string, unknown>): {
    newEnabledMap: Record<string, boolean>;
    newExpandedMap: Record<string, boolean>;
  } {
    const currentEnabledMap = this._sectionEnabledMap();
    const currentExpandedMap = this._sectionExpandedMap();
    const newEnabledMap: Record<string, boolean> = {};
    const newExpandedMap: Record<string, boolean> = {};

    for (const group of this.definitionGroups()) {
      const hasValue = group.definitions.some(def => !isEmptyValue(currentValues[def.key]));
      const enabled = currentEnabledMap[group.sectionKey] ?? hasValue;
      const expanded = currentExpandedMap[group.sectionKey] ?? enabled;
      newEnabledMap[group.sectionKey] = enabled;
      newExpandedMap[group.sectionKey] = expanded;
    }

    return { newEnabledMap, newExpandedMap };
  }

  private buildForm(defs: CustomPropertyDefinitionResponse[]): void {
    const currentValues = this.values();
    this.controlsMap.clear();

    const { newEnabledMap, newExpandedMap } = this.computeSectionMaps(currentValues);
    this._sectionEnabledMap.set(newEnabledMap);
    this._sectionExpandedMap.set(newExpandedMap);

    for (const def of defs) {
      const sectionKey = def.sectionTitle ?? '';
      const sectionEnabled = newEnabledMap[sectionKey] ?? false;
      const initialValue = currentValues[def.key] ?? this.getDefaultValue(def);
      const validators = sectionEnabled ? this.getValidators(def) : [];
      const control = new FormControl(initialValue, { validators });
      this.controlsMap.set(def.key, control);
    }

    for (const [key, control] of this.controlsMap) {
      control.valueChanges.subscribe(() => {
        this.updateFormState();
        if (this.bulkEdit()) {
          this.updateOperation(key);
        } else {
          this.valuesChange.emit(this.getValues());
        }
      });
    }

    this.updateFormState();

    if (!this.bulkEdit()) {
      this.valuesChange.emit(this.getValues());
    }
  }

  /**
   * Whether a property is effectively required — either unconditionally
   * (`def.required`) or conditionally for the active booking type.
   */
  isEffectivelyRequired(def: CustomPropertyDefinitionResponse): boolean {
    if (def.required) return true;
    const bt = this.bookingType();
    if (!bt) return false;
    const requiredFor = (def.config as Record<string, unknown> | null)?.['requiredForBookingTypes'];
    return Array.isArray(requiredFor) && requiredFor.includes(bt);
  }

  private getValidators(def: CustomPropertyDefinitionResponse): ValidatorFn[] {
    if (this.bulkEdit() && this.getMode(def.key) !== 'set') {
      return [];
    }

    if (!this.isEffectivelyRequired(def) || def.propertyType === 'boolean') {
      return [];
    }

    if (def.propertyType === 'multi-select' || def.propertyType === 'tags' || this.isMultiReference(def)) {
      return [nonEmptyArrayValidator];
    }

    return [Validators.required];
  }

  private updateValidators(defs: CustomPropertyDefinitionResponse[]): void {
    for (const def of defs) {
      const control = this.controlsMap.get(def.key);
      if (!control) continue;
      const sectionKey = def.sectionTitle ?? '';
      const sectionEnabled = this.isSectionEnabled(sectionKey);
      const validators = sectionEnabled ? this.getValidators(def) : [];
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });
    }
    this.updateFormState();
  }

  private updateFormState(): void {
    const enabledDefs = this.definitionGroups()
      .filter(g => this.isSectionEnabled(g.sectionKey))
      .flatMap(g => g.definitions);

    const controls = enabledDefs.map(def => this.controlsMap.get(def.key)).filter((c): c is FormControl => c !== undefined);

    this.isDirty.set(controls.some(c => c.dirty));
    this._controlsValid.set(controls.every(c => c.valid));
  }

  private updateOperation(key: string): void {
    const mode = this.getMode(key);
    const control = this.controlsMap.get(key);

    this.operations.update(current => {
      const next = { ...current };
      if (mode === 'unchanged') {
        delete next[key];
      } else if (mode === 'clear') {
        next[key] = { op: 'clear' };
      } else {
        next[key] = { op: 'set', value: control?.value };
      }
      return next;
    });
    this.operationsChange.emit(this.operations());
  }

  private getDefaultValue(def: CustomPropertyDefinitionResponse): unknown {
    switch (def.propertyType as CustomPropertyType) {
      case 'boolean':
        return (def.config as Record<string, unknown> | null)?.['defaultValue'] ?? false;
      case 'tags':
      case 'multi-select':
        return [];
      case 'reference':
        return this.isMultiReference(def) ? [] : null;
      case 'number':
        return null;
      default:
        return null;
    }
  }

  private loadReferenceOptions(entityTypes: CustomPropertyReferenceEntityType[]) {
    const uniqueEntityTypes = Array.from(new Set(entityTypes)).filter(entityType =>
      customPropertyReferenceEntityTypeValues.includes(entityType)
    );

    if (uniqueEntityTypes.length === 0) {
      return of({} as Record<string, ReferenceOption[]>);
    }

    const requests: Partial<Record<CustomPropertyReferenceEntityType, Observable<ReferenceOption[]>>> = {};

    if (uniqueEntityTypes.includes('location')) {
      requests['location'] = this.definitionsApi.listReferenceOptions(this.entityType(), 'location').pipe(
        map(response => response.map(option => this.toReferenceOption(option))),
        catchError(() => of([]))
      );
    }

    if (uniqueEntityTypes.includes('skill')) {
      requests['skill'] = this.definitionsApi.listReferenceOptions(this.entityType(), 'skill').pipe(
        map(response => response.map(option => this.toReferenceOption(option))),
        catchError(() => of([]))
      );
    }

    return forkJoin(requests) as Observable<Record<string, ReferenceOption[]>>;
  }

  private toReferenceOption(option: ReferenceOptionResponse): ReferenceOption {
    return {
      value: option.value,
      label: option.label,
      icon: option.icon,
      description: option.description,
      color: option.color ?? undefined,
      imageUrl: option.imageUrl ?? null,
      data: option.data ?? null,
    };
  }
}
